import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { WEB_APIS } from "../../../utils/APIS";
import { GET, POST } from "../../../utils/Methods";
import { IDS } from "../../../utils/IDS";

/* =======================
   THUNKS
======================= */

// Get all subscriptions
export const GetAllSubscriptions = createAsyncThunk(
    "subscription/get-all",
    async (filter, { rejectWithValue }) => {
        try {
            const res = await GET(
                `${WEB_APIS.Subscription.List}?page=${filter?.page || IDS.PAGE
                }&per_page=${filter?.per_page || IDS.PERPAGE}`
            );
            return res;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get single subscription
export const GetSingleSubscription = createAsyncThunk(
    "subscription/get-single",
    async (id, { rejectWithValue }) => {
        try {
            const res = await GET(`${WEB_APIS.Subscription.List}/${id}`);
            return res?.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get all coupons
export const GetAllCoupons = createAsyncThunk(
    "subscription/get-all-coupons",
    async (_, { rejectWithValue }) => {
        try {
            const res = await GET(WEB_APIS.Subscription.Coupons);
            return res;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get single coupon
export const GetSingleCoupon = createAsyncThunk(
    "subscription/get-single-coupon",
    async (id, { rejectWithValue }) => {
        try {
            const res = await GET(`${WEB_APIS.Subscription.Coupons}/${id}`);
            return res?.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Apply coupon
export const ApplyCoupon = createAsyncThunk(
    "subscription/apply-coupon",
    async (data, { rejectWithValue }) => {
        try {
            const res = await POST(WEB_APIS.Subscription.ApplyCoupon, data);
            if (res.success) return res;
            return rejectWithValue(res);
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

// Buy subscription
export const BuySubscription = createAsyncThunk(
    "subscription/buy",
    async (data, { rejectWithValue }) => {
        try {
            const res = await POST(WEB_APIS.Subscription.Buy, data);
            if (res.success) return res;
            return rejectWithValue(res);
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

/* =======================
   SLICE
======================= */

export const SubscriptionSlice = createSlice({
    name: "subscription",
    initialState: {
        subscriptions: [],
        coupons: [],
        selected_subscription: null,
        selected_coupon: null,
        applied_coupon: null,

        current_page: IDS.PAGE,
        total_pages: IDS.TOTALPAGES,
        per_page: IDS.PERPAGE,
        total: IDS.TOTAL,

        status: IDS.SLICESTATUS.Idle,
        error: null,
    },
    reducers: {
        clearAppliedCoupon: (state) => {
            state.applied_coupon = null;
        },
    },
    extraReducers: (builder) => {
        builder

            /* ---- Subscriptions ---- */
            .addCase(GetAllSubscriptions.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetAllSubscriptions.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.subscriptions = action.payload?.data || [];
                state.current_page = action.payload?.current_page || IDS.PAGE;
                state.total_pages = action.payload?.total_pages || IDS.TOTALPAGES;
                state.per_page = action.payload?.per_page || IDS.PERPAGE;
                state.total = action.payload?.total || IDS.TOTAL;
            })
            .addCase(GetAllSubscriptions.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(GetSingleSubscription.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetSingleSubscription.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.selected_subscription = action.payload;
            })
            .addCase(GetSingleSubscription.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            /* ---- Coupons ---- */
            .addCase(GetAllCoupons.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(GetAllCoupons.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.coupons = action.payload?.data || [];
            })
            .addCase(GetAllCoupons.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            .addCase(GetSingleCoupon.fulfilled, (state, action) => {
                state.selected_coupon = action.payload;
            })

            /* ---- Apply Coupon ---- */
            .addCase(ApplyCoupon.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(ApplyCoupon.fulfilled, (state, action) => {
                state.status = IDS.SLICESTATUS.Succeeded;
                state.applied_coupon = action.payload?.data;
            })
            .addCase(ApplyCoupon.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            })

            /* ---- Buy Subscription ---- */
            .addCase(BuySubscription.pending, (state) => {
                state.status = IDS.SLICESTATUS.Loading;
            })
            .addCase(BuySubscription.fulfilled, (state) => {
                state.status = IDS.SLICESTATUS.Succeeded;
            })
            .addCase(BuySubscription.rejected, (state, action) => {
                state.status = IDS.SLICESTATUS.Failed;
                state.error = action.payload;
            });
    },
});

/* =======================
   EXPORTS
======================= */

export const { clearAppliedCoupon } = SubscriptionSlice.actions;
export default SubscriptionSlice.reducer;
